/**
 * Prism Geometry Utility
 *
 * Computes equilateral triangular prism geometry for CSS 3D rotations.
 * All prisms in the game share this structure: 3 rectangular faces arranged
 * on an equilateral triangle, rotating around Y (vert) or X (horiz) axis.
 *
 * Geometry (equilateral triangle inscribed in circle of radius R):
 *   face = R × √3        (side length = rotating dimension of each face)
 *   apothem = R / 2       (distance from center to face midpoint)
 *   compensate = (perspective − apothem) / perspective
 */

export type PrismOrientation = 'horiz' | 'vert';

export interface PrismGeometry {
  /** Face dimension along the rotation axis (= circumradius × √3) */
  face: number;
  /** Distance from prism center to face midpoint (= circumradius / 2) */
  apothem: number;
  /** Circumradius of the inscribed equilateral triangle */
  circumradius: number;

  /** Full face rectangle width (vert: face, horiz: elongation) */
  faceW: number;
  /** Full face rectangle height (vert: elongation, horiz: face) */
  faceH: number;

  /** CSS perspective value in px */
  perspective: number;
  /** Scale factor to cancel perspective magnification at translateZ(apothem) */
  compensate: number;

  /** CSS transform for positioning a face at the given angle on the triangle */
  faceTransform(angleDeg: number): string;
  /** CSS transform for the prism body rotation to the given angle */
  bodyTransform(angleDeg: number): string;
}

/**
 * Create prism geometry from circumradius.
 *
 * @param orientation 'vert' (Y-axis rotation) or 'horiz' (X-axis rotation)
 * @param radius      circumradius — triangle inscribed in circle of this radius
 * @param elongation  non-rotating dimension (width for horiz, height for vert)
 * @param perspective CSS perspective distance (default 2000)
 */
export function createPrism(
  orientation: PrismOrientation,
  radius: number,
  elongation: number,
  perspective = 2000,
): PrismGeometry {
  const face = radius * Math.sqrt(3);
  const apothem = radius / 2;
  const compensate = (perspective - apothem) / perspective;

  const faceW = orientation === 'vert' ? face : elongation;
  const faceH = orientation === 'vert' ? elongation : face;

  const axis = orientation === 'vert' ? 'Y' : 'X';

  return {
    face,
    apothem,
    circumradius: radius,
    faceW,
    faceH,
    perspective,
    compensate,
    faceTransform: (angleDeg: number) =>
      `rotate${axis}(${angleDeg}deg) translateZ(${apothem}px)`,
    bodyTransform: (angleDeg: number) =>
      `rotate${axis}(${angleDeg}deg)`,
  };
}

/**
 * Convenience: create prism geometry where face dimension = slot dimension.
 * Derives circumradius as slot / √3 so that face = slot exactly.
 *
 * @param orientation 'vert' (Y-axis rotation) or 'horiz' (X-axis rotation)
 * @param slot        visible slot dimension (width for vert, height for horiz)
 * @param elongation  non-rotating dimension (width for horiz, height for vert)
 * @param perspective CSS perspective distance (default 2000)
 */
export function createPrismFromSlot(
  orientation: PrismOrientation,
  slot: number,
  elongation: number,
  perspective = 2000,
): PrismGeometry {
  return createPrism(orientation, slot / Math.sqrt(3), elongation, perspective);
}
